/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Michael Diponio
 * Created: 26/04/2022
 */

import * as React from 'react';
import Page from '@/wpt-lib/pdf/common/Page';
import { Cell, Row, Table } from '@/wpt-lib/pdf/common/Table';
import { useActivitiesFocusArea, useLabels } from '@/wpt-lib/pdf/useData';
import Heading from '@/wpt-lib/pdf/common/Heading';
import { View } from '@react-pdf/renderer';
import { ActivityFocusArea } from '@/wpt-lib/interfaces/Activity';
import { groupBy, sortBy } from 'lodash';
import NameKPI from '@/wpt-lib/pdf/activity/NameKPI';
import Leadership from '@/wpt-lib/pdf/activity/Leadership';
import Strategy2027 from '@/wpt-lib/pdf/activity/Strategy2027';
import ProgressUpdates from '@/wpt-lib/pdf/ProgressUpdates';
import Collaborators from '@/wpt-lib/pdf/activity/Collaborators';
import ExistingResearch from '@/wpt-lib/pdf/activity/research/ExistingResearch';
import NewResearch from '@/wpt-lib/pdf/activity/research/NewResearch';
import ResearchBenchmark from '@/wpt-lib/pdf/activity/research/ResearchBenchmark';
import Publications from '@/wpt-lib/pdf/activity/research/Publications';
import Supervision from '@/wpt-lib/pdf/activity/research/Supervision';
import NonTraditionalResearch from '@/wpt-lib/pdf/activity/research/NonTraditionalResearch';
import Other from '@/wpt-lib/pdf/activity/research/Other';
import ProfessionalDevelopment from '@/wpt-lib/pdf/activity/research/ProfessionalDevelopment';
import Comments from '@/wpt-lib/pdf/Comments';
import ExpectedOutcomes from '@/wpt-lib/pdf/activity/ExpectedOutcomes';
import PathwayToEngagement from '@/wpt-lib/pdf/activity/research/PathwaytoEngagement';
import SubHeading from '@/wpt-lib/pdf/common/SubHeading';

const ActivityTitles: { [type: string]: string } = {
    'existing-research-projects': 'Existing Research Project',
    'new-research-projects': 'New Research Project',
    publications: 'Research Publications',
    'supervisions-completions': 'Supervision',
    'non-traditional-research-outputs': 'Non-Traditional Research Output',
    other: 'Other Research Activity',
    'professional-development': 'Professional Development Research Activity',
};

// eslint-disable-next-line
const ActivityComponents: { [type: string]: React.ComponentType<any> } = {
    'existing-research-projects': ExistingResearch,
    'new-research-projects': NewResearch,
    publications: Publications,
    'supervisions-completions': Supervision,
    'non-traditional-research-outputs': NonTraditionalResearch,
    other: Other,
    'professional-development': ProfessionalDevelopment,
};

const ResearchActivities = () => {
    const order = Object.keys(ActivityTitles);
    const research = sortBy(
        Object.entries(
            groupBy(useActivitiesFocusArea(ActivityFocusArea.RESEARCH), (a) => `${a.data.researchScholarship}`),
        ),
        (e) => order.indexOf(e[0]),
    );

    const titleLabels = useLabels('teachingEducational');

    return research.length > 0 ? (
        <Page>
            <Heading text="Research Activities" />

            {research.map(([type, typeActivities]) => (
                <View key={type}>
                    <Table cols={4}>
                        <Row>
                            <SubHeading level={1} text={titleLabels[type] ?? ActivityTitles[type]} />
                        </Row>
                        {typeActivities.map((activity, i) => (
                            <View key={i}>
                                <NameKPI {...activity.data} />
                                <PathwayToEngagement {...activity.data} />
                                {type in ActivityComponents &&
                                    React.createElement(ActivityComponents[type], {
                                        ...activity.data,
                                    })}
                                <ExpectedOutcomes {...activity.data} />
                                <Collaborators {...activity.data} />
                                <Leadership {...activity.data} />
                                <ResearchBenchmark {...activity.data} />
                                <Strategy2027 {...activity.data} />
                                <ProgressUpdates item={activity.workplanItem} />
                                <Comments comments={activity.workplanItem.comments} />
                                <Row>
                                    <Cell bg="#9faabd" />
                                </Row>
                            </View>
                        ))}
                    </Table>
                </View>
            ))}
        </Page>
    ) : null;
};

export default ResearchActivities;
